import { Component, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { Observable, observable, Subject, Subscription } from 'rxjs';

import { LoginService } from '../../../../pages/login/login.service';
import { AppService, UserService } from '../../../../shared/service/user.service';

import { ConfirmService } from '../../../../shared/components/confirm/confirm.service';
import { timeout } from 'rxjs/operators';
import { AppSubject } from '../../../../shared/app-subject';
import { ToastService } from '../../../../shared/components/toast/toast.service';
import { InvoiceService } from '../../../customer/invoice/invoice.service';
import { AgreementSigningHandlerService } from './AgreementSigningHandler.service';

interface RunValue {
  idValue?: string;
  idValues?: any;
  actionType: string;
  issueStatusId?: string;
  taxCode?: string;
  requireLoginKey?: boolean;
  suppressPopup?: boolean;
  referenceCode?: string;
  agreementContentId?: string;
  signedByRole?: 'SELLER' | 'BUYER';
  signedByPartyId?: string;
}

interface SigningAttempt {
  attemptId: string;
  userId: string;
  signingType: string;
  idValue: string;
  createdAt: number;
}

declare global {
  interface Window {
    http: HttpClient;
  }
}

@Injectable({
  providedIn: `root`
})
export class SigningService {

  // Các actionType thuộc nhóm ký/phát hành hóa đơn cần kiểm tra điều kiện hình thức kinh doanh.
  // Các actionType khác (biên bản, tờ khai, TBSS, CTS...) không nằm trong danh sách nên không bị ảnh hưởng.
  private static readonly INVOICE_SIGNING_ACTION_TYPES = [
    'InvoiceSales',
    'InvoiceTax',
    'InvoiceVatPos',
    'InvoiceSalePos',
    'InvoicePublicAsset',
    'invoiceBatchSigning',
    'posBatchSigning'
  ];

  readonly errorSubject: Subject<any>;
  readonly displaySubject: Subject<any>;
  addCTSSubscription?: Subscription;
  private isSigningInProgress: boolean = false;
  private batchResumePrompted = false;
  private url: string = window.location.origin + '/api/rest/s1';
  private readonly STORAGE_KEY = 'signing_state'; // Key để lưu vào localStorage

  constructor(
    private http: HttpClient,
    private backend: HttpBackend,
    private confirmService: ConfirmService,
    private loginService: LoginService,
    private invoiceService: InvoiceService,
    private router: Router,
    private toastService: ToastService,
    private agreementSigningHandler: AgreementSigningHandlerService
  ) {
    this.http = new HttpClient(this.backend);
    this.errorSubject = new Subject<any>();
    this.displaySubject = new Subject<any>();
    this.checkPendingSigning();

  }

  runWithData(runValue: RunValue): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        this.prepareSigningAttempt(runValue);
      } catch (error) {
        this.showSigningAttemptError(error);
        reject(error);
        return;
      }
      window.http = this.http;
      this.isSigningInProgress = true;
      UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(runValue));
      // Lưu trạng thái ký vào localStorage
      this.saveSigningState(runValue);

      this.http.get('http://localhost:13589/test').pipe(timeout(1500)).subscribe({
        next: (res: any) => {
          if (res?.receiveDataFromBrowser === 'Y') {
            const data = {
              signingType: runValue.actionType,
              idValue: runValue.idValue,
              taxCode: runValue.taxCode || UserService.getUser().partyTaxId,
              signingAttemptId: this.prepareSigningAttempt(runValue)?.attemptId,
              responseContentType: 'application/json'
            };

            this.http.put(`${this.url}/iam-common/signing`, data, { headers: { 'x-csrf-token': AppService.get('MOQUI_SESSION_TOKEN') } }).subscribe({
              next: (result: any) => {
                this.displaySubject.next('open');
                this.http.post('http://localhost:13589/signing', result).subscribe({
                  next: (response: any) => {
                    if (response?.success === true) {
                      this.completeSigningWithData(response, resolve, reject);
                    } else if (response?.success === false) {
                      this.cancelSigningWithData(runValue, reject);
                    } else {
                      this.clearSigningState();
                      reject(new Error('No response'));
                    }
                  },
                  error: (error) => this.handleErrorWithData(error, reject)
                });
              },
              error: (error) => {
                this.handleSigningAttemptError(error, runValue);
                this.handleErrorWithData(error, reject);
              }
            });
          }
        },
        error: (err: any) => this.handleTestErrorWithData(err, runValue, reject)
      });
    });
  }


  run(runValue: RunValue): Promise<void> {
    // Với nhóm ký hóa đơn: kiểm tra điều kiện hình thức kinh doanh trước khi mở form ký.
    if (this.isInvoiceSigning(runValue)) {
      return new Promise<void>((resolve, reject) => {
        this.invoiceService.getBusinessEligibility().subscribe({
          next: (eligibility: any) => {
            if (eligibility && eligibility.isEligible === 'N') {
              this.showBusinessEligibilityDialog(eligibility);
              reject(eligibility);
            } else {
              this.startSigning(runValue).then(resolve).catch(reject);
            }
          },
          error: (err: any) => {
            // Không xác định được điều kiện => để BE chốt chặn cứng, vẫn cho mở luồng ký.
            this.startSigning(runValue).then(resolve).catch(reject);
          }
        });
      });
    }
    return this.startSigning(runValue);
  }

  private isInvoiceSigning(runValue: RunValue): boolean {
    return SigningService.INVOICE_SIGNING_ACTION_TYPES.includes(runValue?.actionType);
  }

  private showBusinessEligibilityDialog(eligibility: any): void {
    const message = eligibility?.message
      || 'Tài khoản chưa cập nhật hình thức kinh doanh. Vui lòng cập nhật trước khi ký hóa đơn.';
    this.confirmService.setConfirmDialog({
      message,
      header: 'Không thể ký hóa đơn',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Cập nhật ngay',
      rejectLabel: 'Đóng',
      keepLabels: true,
      accept: () => {
        this.router.navigateByUrl('/customer/system/organization-info?edit=Y');
      }
    } as any);
  }

  private startSigning(runValue: RunValue): Promise<void> {
    return new Promise((resolve, reject) => {
      AppSubject.setReloadData('')
      try {
        this.prepareSigningAttempt(runValue);
      } catch (error) {
        this.showSigningAttemptError(error);
        reject(error);
        return;
      }
      this.saveSigningState(runValue);
      UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(runValue));

      if (UserService.get('SIGNING_TYPES') !== 'NULL' && UserService.get('SIGNING') !== 'newCert' && !runValue.suppressPopup) {
        this.displaySubject.next('form1');
      }
      if (this.isPdfRecordSigning(runValue) && UserService.get('SIGNING') === 'UsbTokenSigning') {
        this.handleAgreementPdfUsbSigning(runValue, resolve, reject);
        return;
      }
      if (UserService.get('SIGNING') || UserService.get('SIGNING_TYPES') === 'NULL') {
        if (UserService.get('SIGNING') === 'UsbTokenSigning' || UserService.get('SIGNING') === 'newCert' || UserService.get('SIGNING_TYPES') === 'NULL') {
          this.http.get('http://localhost:13589/test').pipe(timeout(1000)).subscribe({
            next: (res: any) => {
              if (res?.receiveDataFromBrowser === "Y") {
                const data = {
                  signingType: runValue.actionType,
                  idValue: runValue.idValue,
                  taxCode: runValue.taxCode || UserService.getUser().partyTaxId,
                  signingAttemptId: this.prepareSigningAttempt(runValue)?.attemptId,
                  responseContentType: "application/json"
                };
                const url = window.location.origin + '/api/rest/s1';
                this.http.put(`${url}/iam-common/signing`, data, { headers: { "x-csrf-token": AppService.get('MOQUI_SESSION_TOKEN') } }).subscribe({
                  next: (result: any) => {
                    this.displaySubject.next('form3');
                    this.http.post('http://localhost:13589/signing', result).subscribe({
                      next: (response: any) => {
                        if (response?.success) {
                          this.http.post(`${url}/iam-common/complete-signing`, response, { headers: { "x-csrf-token": AppService.get('MOQUI_SESSION_TOKEN') } },).subscribe({
                            next: (finalResult: any) => {
                              if (finalResult?.idValue) {
                                this.clearSigningAttempt(runValue);
                                AppSubject.setReloadData('success')
                                this.clearSigningState()
                                this.displaySubject.next('closeForm3');
                                resolve();
                              } else if (finalResult?.errorMessage) {
                                this.confirmService.setConfirmDialog({
                                  message: `[${finalResult?.errorCode}] ${finalResult?.errorMessage}`,
                                  header: 'Ký điện tử không thành công',
                                  icon: 'pi pi-exclamation-triangle',
                                  rejectLabel: 'Đóng',
                                  acceptVisible: false
                                });
                                AppSubject.setReloadData('error')
                                this.displaySubject.next('closeForm3');
                                reject();
                              } else {
                                reject();
                              }
                            }, error: error => {
                              this.confirmService.setConfirmDialog({
                                message: `[${error?.error.errorCode}] ${error?.error.errors}`,
                                header: 'Ký điện tử không thành công',
                                icon: 'pi pi-exclamation-triangle',
                                rejectLabel: 'Đóng',
                                acceptVisible: false
                              });
                              AppSubject.setReloadData('error')
                              this.displaySubject.next('closeForm3');
                              reject();
                            }
                          })
                        } else if (response?.errorMessage) {
                          this.confirmService.setConfirmDialog({
                            message: `[${response?.errorCode}] ${response?.errorMessage}`,
                            header: 'Ký điện tử không thành công',
                            icon: 'pi pi-exclamation-triangle',
                            rejectLabel: 'Đóng',
                            acceptVisible: false
                          });
                          const input = {
                            success: "N",
                            idValue: runValue.idValue,
                            signingType: runValue.actionType,
                            signingAttemptId: this.getSigningAttempt(runValue)?.attemptId
                          }
                          this.http.post(`${url}/iam-common/complete-signing`, input, { headers: { "x-csrf-token": AppService.get('MOQUI_SESSION_TOKEN') } },).subscribe({
                            next: (finalResult: any) => {
                              if (finalResult?.idValue) {
                                this.clearSigningAttempt(runValue);
                                this.clearSigningState()
                                AppSubject.setReloadData('error')
                                this.displaySubject.next('closeForm3');
                                resolve();
                              } else {
                                AppSubject.setReloadData('error')
                                this.displaySubject.next('closeForm3');
                                reject();
                              }
                            }, error: error => {
                              reject();
                            }
                          })
                        } else {
                          reject();
                        }
                      }, error: error => {
                        this.handleSigningAttemptError(error, runValue);
                        reject();
                      }
                    })
                  }, error: error => {
                    this.handleSigningAttemptError(error, runValue);
                    this.confirmService.setConfirmDialog({
                      message: `[${error?.error.errorCode}] ${error?.error.errors}`,
                      header: 'Ký điện tử không thành công',
                      icon: 'pi pi-exclamation-triangle',
                      rejectLabel: 'Đóng',
                      acceptVisible: false
                    });
                    reject();
                  }
                })
              } else {
                let loginKey = '';
                if (runValue.requireLoginKey !== false) {
                  const apiKey: any = new Promise(resolve => {
                    this.loginService.getApiKey().subscribe(res => {

                      resolve(res);
                    });
                  });
                  loginKey = apiKey.token;
                }

                const data = {
                  baseUrl: window.location.origin + '/api',
                  loginKey,
                  csrfToken: AppService.get('MOQUI_SESSION_TOKEN'),
                  actionType: runValue.actionType,
                  idValue: runValue.idValue,
                  taxCode: runValue.taxCode || UserService.getUser().partyTaxId,
                  signingAttemptId: this.prepareSigningAttempt(runValue)?.attemptId
                };
                this.http.post('http://localhost:13589/signing', data).subscribe({
                  next: (response: any) => {

                    if (response?.success) {
                      this.clearSigningAttempt(runValue);
                      this.clearSigningState()

                      AppSubject.setReloadData('success')
                      resolve();
                    } else if (response?.errorMessage) {
                      this.confirmService.setConfirmDialog({
                        message: `[${response?.errorCode}] ${response?.errorMessage}`,
                        header: 'Ký điện tử không thành công',
                        icon: 'pi pi-exclamation-triangle',
                        rejectLabel: 'Đóng',
                        acceptVisible: false
                      });
                      AppSubject.setReloadData('error')
                      reject();
                    } else {
                      reject();
                    }
                  }, error: error => {
                    this.handleSigningAttemptError(error, runValue);
                    this.errorSubject.next(error);
                    reject();
                  }
                })
              }
              UserService.set(UserService.KEY.SIGNING, '')
            },
            error: async error => {
              if (error.status === 200) {
                {
                  let loginKey = '';
                  if (runValue.requireLoginKey !== false) {
                    const apiKey: any = await new Promise(resolve => {
                      this.loginService.getApiKey().subscribe(res => {

                        resolve(res);
                      });
                    });
                    loginKey = apiKey.token;
                  }

                  const data = {
                    baseUrl: window.location.origin + '/api',
                    loginKey,
                    csrfToken: AppService.get('MOQUI_SESSION_TOKEN'),
                    actionType: runValue.actionType,
                    idValue: runValue.idValue,
                    taxCode: runValue.taxCode || UserService.getUser().partyTaxId,
                    signingAttemptId: this.prepareSigningAttempt(runValue)?.attemptId
                  };
                  this.http.post('http://localhost:13589/signing', data).subscribe({
                    next: (response: any) => {

                      if (response?.success) {
                        this.clearSigningAttempt(runValue);
                        AppSubject.setReloadData('success')
                        this.clearSigningState()
                        resolve();
                      } else if (response?.errorMessage) {
                        this.confirmService.setConfirmDialog({
                          message: `[${response?.errorCode}] ${response?.errorMessage}`,
                          header: 'Ký điện tử không thành công',
                          icon: 'pi pi-exclamation-triangle',
                          rejectLabel: 'Đóng',
                          acceptVisible: false
                        });
                        AppSubject.setReloadData('error')
                        reject();
                      } else {
                        reject();
                      }
                    }, error: error => {
                      this.handleSigningAttemptError(error, runValue);
                      this.errorSubject.next(error);
                      reject();
                    }
                  })
                }
              } else {
                this.errorSubject.next(error);
                reject();
              }
            }

          })
        } else {
          const url = window.location.origin + '/api/rest/s1';
          this.http.get(`${url}/iam-common/remote-signing?partyClassificationId=${UserService.get('SIGNING')}`, { headers: { "x-csrf-token": AppService.get('MOQUI_SESSION_TOKEN') } }).subscribe({
            next: (result: any) => {
              if (result.listData) {
                UserService.set(UserService.KEY.REMOTE_SIGNING, JSON.stringify(result.listData))
                this.displaySubject.next('form2');
              }
            }
            , error: async error => {
            }
          })
        }
      } else {
        UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(runValue))
      }
    });
  }

  private isPdfRecordSigning(runValue?: RunValue): boolean {
    return runValue?.actionType === 'agreementInvoiceAdj'
      && !!(runValue.referenceCode || runValue.agreementContentId);
  }

  private handleAgreementPdfUsbSigning(runValue: RunValue, resolve: () => void, reject: (reason?: any) => void): void {
    this.agreementSigningHandler.signAgreement({
      referenceCode: runValue.referenceCode || runValue.agreementContentId || '',
      signedByRole: runValue.signedByRole || 'SELLER',
      signedByPartyId: runValue.signedByPartyId,
      onSuccess: () => {
        AppSubject.setReloadData('success');
        this.clearSigningState();
        resolve();
      },
      onError: (error: any) => {
        AppSubject.setReloadData('error');
        reject(error);
      }
    }).catch(reject);
  }

  private saveSigningState(runValue: RunValue): void {
    const state = {
      idValue: runValue.idValue,
      actionType: runValue.actionType,
      timestamp: Date.now() // Lưu thời gian để kiểm tra tính hợp lệ
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  // Xóa trạng thái ký khỏi localStorage
  private clearSigningState(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.isSigningInProgress = false;
  }

  // Kiểm tra và xử lý trạng thái ký bị gián đoạn sau khi trang tải lại
  private checkPendingSigning(): void {
    const storedState = localStorage.getItem(this.STORAGE_KEY);
    if (storedState) {
      const state = JSON.parse(storedState);
      const runValue: RunValue = {
        idValue: state.idValue,
        actionType: state.actionType
      };
      if (this.isBatchSigning(runValue) && this.getSigningAttempt(runValue)) {
        return;
      }
      const timeElapsed = Date.now() - state.timestamp;
      const timeoutThreshold = 30000; // 30 giây, tùy chỉnh theo nhu cầu

      // Nếu trạng thái tồn tại và chưa quá thời gian timeout, gọi API hủy
      if (timeElapsed < timeoutThreshold) {
        this.cancelSigningAfterReload(runValue);
      } else {
        // Nếu quá thời gian, xóa trạng thái cũ
        this.clearSigningState();
      }
    }
  }

  private cancelSigningAfterReload(runValue: RunValue): void {
    const input = {
      success: 'N',
      idValue: runValue.idValue,
      signingType: runValue.actionType,
      signingAttemptId: this.getSigningAttempt(runValue)?.attemptId
    };
    const headers = {
      'x-csrf-token': AppService.get('MOQUI_SESSION_TOKEN'),
      'Content-Type': 'application/json'
    };

    this.http.post(`${this.url}/iam-common/complete-signing`, input, { headers }).subscribe({
      next: (finalResult) => {
        console.log('Signing cancelled after reload:', finalResult);
        this.clearSigningState();
        this.confirmService.setConfirmDialog({
          message: 'Quá trình ký đã bị gián đoạn do tải lại trang.',
          header: 'Thông báo',
          icon: 'pi pi-info-circle',
          rejectLabel: 'Đóng',
          acceptVisible: false
        });
      },
      error: (err) => {
        console.error('Failed to cancel signing after reload:', err);
        this.clearSigningState();
      }
    });
  }

  private handleTestErrorWithData(err: any, runValue: RunValue, reject: (data: any) => void): void {
    this.errorSubject.next(err);
    this.displaySubject.next('signProgress');
    this.loginService.getApiKey().subscribe((response) => {
      if (response.token) {
        location.href = `${AppService.KEY.SIGN}:${response.token},${AppService.get(AppService.KEY.MOQUI_SESSION_TOKEN)},${runValue.actionType},${runValue.idValue}`;
      }
    });
    this.clearSigningState();
    reject(err);
  }

  private handleErrorWithData(error: any, reject: (data: any) => void): void {
    this.confirmService.setConfirmDialog({
      message: `[${error?.error?.errorCode}] ${error?.error?.errors}`,
      header: 'Ký điện tử không thành công',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Đóng',
      acceptVisible: false
    });
    this.errorSubject.next(error);
    this.displaySubject.next('close');
    this.clearSigningState();
    reject(error);
  }

  private completeSigningWithData(response: any, resolve: (data: any) => void, reject: (data: any) => void): void {
    this.http.post(`${this.url}/iam-common/complete-signing`, response, { headers: { 'x-csrf-token': AppService.get('MOQUI_SESSION_TOKEN') } }).subscribe({
      next: (finalResult: any) => {
        if (finalResult?.idValue) {
          this.clearSigningAttempt();
          this.toastService.success();
          this.displaySubject.next('close');
          this.clearSigningState();
          resolve(finalResult); // ✅ Return data
        } else if (finalResult?.errorMessage) {
          this.showError(finalResult.errorCode, finalResult.errorMessage);
          this.displaySubject.next('close');
          this.clearSigningState();
          reject(finalResult);
        }
      },
      error: (error) => this.handleErrorWithData(error, reject)
    });
  }

  private cancelSigningWithData(runValue: RunValue, reject: (data: any) => void): void {
    const input = {
      success: 'N',
      idValue: runValue.idValue,
      signingType: runValue.actionType,
      signingAttemptId: this.getSigningAttempt(runValue)?.attemptId
    };
    this.http.post(`${this.url}/iam-common/complete-signing`, input, { headers: { 'x-csrf-token': AppService.get('MOQUI_SESSION_TOKEN') } }).subscribe({
      next: (finalResult: any) => {
        this.clearSigningAttempt(runValue);
        this.errorSubject.next(finalResult);
        this.displaySubject.next('close');
        this.clearSigningState();
        reject(finalResult); // ✅ Return error data
      },
      error: (err) => {
        console.error('Failed to cancel signing:', err);
        this.clearSigningState();
        reject(err);
      }
    });
  }

  private showError(errorCode: string, errorMessage: string): void {
    this.confirmService.setConfirmDialog({
      message: `[${errorCode}] ${errorMessage}`,
      header: 'Ký điện tử không thành công',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Đóng',
      acceptVisible: false
    });
  }

  remoteSigning(data: any): Observable<any> {
    const runValue: RunValue | undefined = data?.idValue && data?.signingType ? {
      idValue: data?.idValue,
      actionType: data?.signingType,
      issueStatusId: data?.issueStatusId
    } as RunValue : this.getCurrentRunValue();
    const isIntrustAgreement = runValue?.actionType === 'agreementInvoiceAdj'
      && data?.partyClassificationId === 'IntrustRemoteSigning';
    const payload = isIntrustAgreement
      ? {
          referenceCode: runValue?.referenceCode,
          agreementContentId: runValue?.agreementContentId,
          partyOtherIndentId: data.partyOtherIndentId,
          pinCode: data.pinCode
        }
      : Object.assign({}, data, {
          // The signing-method dialog only returns its own fields. Preserve the
          // original signing target so remote signing can dispatch the correct
          // agreement/invoice service instead of sending an empty idValue.
          signingType: runValue?.actionType || data?.signingType,
          idValue: runValue?.idValue || data?.idValue,
          referenceCode: runValue?.referenceCode || data?.referenceCode,
          agreementContentId: runValue?.agreementContentId || data?.agreementContentId,
          signingAttemptId: this.prepareSigningAttempt(runValue)?.attemptId
        });
    const url = isIntrustAgreement
      ? `/api/rest/s1/iam-intrust/agreement-content-signing`
      : `/api/rest/s1/iam-common/remote-signing`;
    const obs = this.http.post(url, payload, { headers: { "x-csrf-token": AppService.get('MOQUI_SESSION_TOKEN') } });
    return new Observable(subscriber => {
      obs.subscribe({
        next: (val) => {
          this.clearSigningAttempt(runValue);
          subscriber.next(val);
        },
        error: (err) => {
          this.handleSigningAttemptError(err, runValue);
          subscriber.error(err);
        },
        complete: () => subscriber.complete()
      });
    });
  }

  promptResumeBatchSigningAttempt(): void {
    if (this.batchResumePrompted) {
      return;
    }

    const attempt = this.findLatestBatchSigningAttempt();
    if (!attempt) {
      return;
    }

    this.batchResumePrompted = true;
    this.confirmService.setConfirmDialog({
      message: 'Đang thực hiện ký hóa đơn hàng loạt, bạn có muốn hoàn thành quy trình ký này không?',
      header: 'Ký hóa đơn hàng loạt',
      icon: 'pi pi-question-circle',
      accept: () => this.resumeBatchSigningAttempt(attempt),
      reject: () => this.rejectBatchSigningAttempt(attempt)
    });
  }

  private prepareSigningAttempt(runValue?: RunValue): SigningAttempt | undefined {
    if (!this.shouldUseSigningAttempt(runValue)) {
      return undefined;
    }

    const key = this.getSigningAttemptKey(runValue);
    if (!key) {
      return undefined;
    }

    const stored = sessionStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }

    const issueStatusId = runValue?.issueStatusId || (runValue?.actionType === 'toKhai100' ? 'EiurNotSent' : '');
    if (!this.canStartNewAttempt(runValue?.actionType || '', issueStatusId)) {
      throw new Error(
        issueStatusId === 'InvIssuing'
          ? 'Hóa đơn đang được phát hành tại một phiên làm việc khác.'
          : 'Trạng thái hóa đơn không cho phép thực hiện ký.'
      );
    }

    const attempt: SigningAttempt = {
      attemptId: this.createUuid(),
      userId: this.getSigningUserId(),
      signingType: runValue?.actionType || '',
      idValue: runValue?.idValue || '',
      createdAt: Date.now()
    };

    sessionStorage.setItem(key, JSON.stringify(attempt));
    return attempt;
  }

  private getSigningAttempt(runValue?: RunValue): SigningAttempt | undefined {
    const key = this.getSigningAttemptKey(runValue);
    if (!key) {
      return undefined;
    }
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : undefined;
  }

  private findLatestBatchSigningAttempt(): SigningAttempt | undefined {
    const prefix = `signing-attempt:${this.getSigningUserId()}:`;
    const attempts: SigningAttempt[] = [];

    for (let index = 0; index < sessionStorage.length; index++) {
      const key = sessionStorage.key(index) || '';
      if (!key.startsWith(prefix)) {
        continue;
      }

      const parts = key.split(':');
      const signingType = parts[2];
      if (!['invoiceBatchSigning', 'posBatchSigning'].includes(signingType)) {
        continue;
      }

      const stored = sessionStorage.getItem(key);
      if (!stored) {
        continue;
      }

      try {
        const attempt = JSON.parse(stored) as SigningAttempt;
        if (attempt?.attemptId && attempt?.idValue && attempt?.signingType) {
          attempts.push(attempt);
        }
      } catch {
      }
    }

    return attempts.sort((left, right) => (right.createdAt || 0) - (left.createdAt || 0))[0];
  }

  private resumeBatchSigningAttempt(attempt: SigningAttempt): void {
    const runValue = this.getBatchSigningRunValue(attempt);

    UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(runValue));
    UserService.set(UserService.KEY.SIGNING, 'UsbTokenSigning');
    this.run(runValue).catch(() => {
      UserService.set(UserService.KEY.SIGNING, '');
    });
  }

  private completeSigning(runValue: RunValue, success: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = window.location.origin + '/api/rest/s1';
      const input = {
        success,
        idValue: runValue.idValue,
        signingType: runValue.actionType,
        signingAttemptId: this.getSigningAttempt(runValue)?.attemptId
      };

      this.http.post(`${url}/iam-common/complete-signing`, input, {
        headers: { 'x-csrf-token': AppService.get('MOQUI_SESSION_TOKEN') }
      }).subscribe({
        next: (finalResult: any) => {
          if (finalResult?.idValue) {
            this.clearSigningAttempt(runValue);
            AppSubject.setReloadData(success === 'Y' ? 'success' : 'error');
            this.displaySubject.next('closeForm3');
            resolve();
          } else {
            reject();
          }
        },
        error: error => reject(error)
      });
    });
  }

  private rejectBatchSigningAttempt(attempt: SigningAttempt): void {
    const runValue = this.getBatchSigningRunValue(attempt);

    UserService.set(UserService.KEY.RUN_VALUE, JSON.stringify(runValue));
    this.completeSigning(runValue, 'N').finally(() => {
      this.batchResumePrompted = false;
    });
  }

  private getBatchSigningRunValue(attempt: SigningAttempt): RunValue {
    return {
      idValue: attempt.idValue,
      actionType: attempt.signingType,
      issueStatusId: 'InvNotIssued',
      suppressPopup: true
    };
  }

  private clearSigningAttempt(runValue?: RunValue): void {
    const key = this.getSigningAttemptKey(runValue || this.getCurrentRunValue());
    if (key) {
      sessionStorage.removeItem(key);
    }
  }

  private getSigningAttemptKey(runValue?: RunValue): string {
    if (!this.shouldUseSigningAttempt(runValue) || !runValue?.idValue) {
      return '';
    }
    return `signing-attempt:${this.getSigningUserId()}:${runValue.actionType}:${runValue.idValue}`;
  }

  private shouldUseSigningAttempt(runValue?: RunValue): boolean {
    if (!runValue?.actionType || !runValue?.idValue) {
      return false;
    }
    return runValue.actionType === 'toKhai100'
      || runValue.actionType === 'invoiceBatchSigning'
      || runValue.actionType === 'posBatchSigning'
      || runValue.actionType.indexOf('Invoice') === 0;
  }

  private isBatchSigning(runValue?: RunValue): boolean {
    return ['invoiceBatchSigning', 'posBatchSigning'].includes(runValue?.actionType || '');
  }

  private canStartNewAttempt(signingType: string, issueStatusId: string): boolean {
    if (signingType === 'toKhai100') {
      return !issueStatusId || ['EiurNotSent', 'EiurSellerSigningError', 'EiurDataNotValid'].includes(issueStatusId);
    }
    if (!issueStatusId) {
      return true;
    }
    return ['InvNotIssued', 'InvIssuedError'].includes(issueStatusId);
  }

  private getCurrentRunValue(): RunValue | undefined {
    try {
      const raw = UserService.get(UserService.KEY.RUN_VALUE);
      return raw ? JSON.parse(raw) : undefined;
    } catch {
      return undefined;
    }
  }

  private getSigningUserId(): string {
    const user = UserService.getUser() || {};
    return user.userId || user.userLoginId || user.partyId || user.partyTaxId || 'anonymous';
  }

  private createUuid(): string {
    const cryptoRef = window.crypto as any;
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
      const random = Math.random() * 16 | 0;
      const value = char === 'x' ? random : (random & 0x3 | 0x8);
      return value.toString(16);
    });
  }

  private handleSigningAttemptError(error: any, runValue?: RunValue): void {
    const body = error?.error || error;
    const code = body?.code || body?.errorCode || body?.error || '';
    if (code === 'SIGNING_ATTEMPT_NOT_OWNER') {
      this.clearSigningAttempt(runValue);
    }
  }

  private showSigningAttemptError(error: any): void {
    const body = error?.error || error;
    this.confirmService.setConfirmDialog({
      message: body?.errorMessage || body?.message || `${body}`,
      header: 'Ký điện tử không thành công',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Đóng',
      acceptVisible: false
    });
  }

  signingType(): Observable<any> {
    return this.http.get(`/api/rest/s1/iam-common/signing-type`);
  }

  digitalCert(id: string): Observable<any> {
    return this.http.get(`/api/rest/s1/iam-common/remote-signing?partyClassificationId=${id}`);
  }
}
